'use strict';

const libCustomerConfigScreen = require('./CustomerConfigScreen-Provider.js');

// Read by the API on every document create (api/headlight/ts_source/bundles/contentmanagement/DocumentAccess.ts
// assignmentByType): `{ Enabled, ByType: { <form type hash>: { Roles: [ <Role.PermissionLevel>, … ] } } }`.
const NAMESPACE = 'EntityOwnershipAssignment';
const NAMESPACE_VALUE = 'Document';
// PermissionLevel values that mark an administrative tier rather than name a role.
const TIER_LEVELS = [ 'CustomerAdmin', 'RoleManager', 'SuperAdmin' ];

/**
 * @typedef {{ Code: string, Names: Array<string> }} FormAccessRole - roles sharing one permission level
 * @typedef {{ Hash: string, Name: string, Standard: boolean }} FormAccessType
 */

/**
 * The role codes listed for each form type, from a loaded configuration. A type whose entry isn't an object grants
 * nothing on the server, so it is read as having no roles.
 *
 * @param {Record<string, any>} pConfig
 * @return {Record<string, Array<string>>}
 */
function rolesByType(pConfig)
{
	const tmpByType = (pConfig && pConfig.ByType && (typeof pConfig.ByType === 'object')) ? pConfig.ByType : {};
	/** @type {Record<string, Array<string>>} */
	const tmpResult = {};
	Object.keys(tmpByType).forEach((pHash) =>
	{
		const tmpEntry = tmpByType[pHash];
		const tmpRoles = (tmpEntry && (typeof tmpEntry === 'object') && Array.isArray(tmpEntry.Roles)) ? tmpEntry.Roles : [];
		tmpResult[pHash] = tmpRoles.filter((pCode) => (typeof pCode === 'string') && pCode.length > 0);
	});
	return tmpResult;
}

/**
 * The configuration to save. Keys the screen has no control for, on the whole value and on each type's entry, are
 * kept. A type left with no roles and nothing else is dropped, since it grants nothing.
 *
 * @param {Record<string, any>} pLoaded - the value as read
 * @param {boolean} pEnabled
 * @param {Record<string, Array<string>>} pRolesByType
 * @return {Record<string, any>}
 */
function buildAssignmentConfig(pLoaded, pEnabled, pRolesByType)
{
	const tmpLoaded = pLoaded || {};
	const tmpLoadedByType = (tmpLoaded.ByType && (typeof tmpLoaded.ByType === 'object')) ? tmpLoaded.ByType : {};
	const tmpByType = {};
	Object.keys(pRolesByType || {}).sort().forEach((pHash) =>
	{
		const tmpLoadedEntry = tmpLoadedByType[pHash];
		const tmpEntry = (tmpLoadedEntry && (typeof tmpLoadedEntry === 'object') && !Array.isArray(tmpLoadedEntry)) ? Object.assign({}, tmpLoadedEntry) : {};
		const tmpRoles = Array.from(new Set(pRolesByType[pHash] || []));
		if (tmpRoles.length > 0)
		{
			tmpEntry.Roles = tmpRoles;
		}
		else
		{
			delete tmpEntry.Roles;
		}
		if (Object.keys(tmpEntry).length > 0)
		{
			tmpByType[pHash] = tmpEntry;
		}
	});
	return Object.assign({}, tmpLoaded, { Enabled: !!pEnabled, ByType: tmpByType });
}

/**
 * FormAccessConfig: the #/FormAccess screen ("Who Can Open New Forms"). For each form type, the roles that are given
 * a new form of that type the moment it is created, so the people holding them can open and edit it without being
 * members of its project. Saved as the customer's `EntityOwnershipAssignment` / `Document` configuration. Opened by
 * the Configure who can open new forms entitlement.
 */
class FormAccessConfigProvider extends libCustomerConfigScreen
{
	/** @return {Record<string, any>} */
	initialState()
	{
		return { Loaded_Config: {}, Enabled: true, RolesByType: {}, Roles: [], FormTypes: [], Selected: {} };
	}

	/**
	 * @param {number} pIDCustomer
	 * @return {Promise<void>}
	 */
	async loadCustomerAsync(pIDCustomer)
	{
		const tmpState = this.state;
		tmpState.Loaded_Config = await this.readCustomerConfigAsync(NAMESPACE, NAMESPACE_VALUE, pIDCustomer);
		this.rememberSnapshot(NAMESPACE, NAMESPACE_VALUE, tmpState.Loaded_Config);
		tmpState.Enabled = (tmpState.Loaded_Config.Enabled !== false);
		tmpState.RolesByType = rolesByType(tmpState.Loaded_Config);
		tmpState.Selected = {};

		const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${Number(pIDCustomer)}~FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/3000`);
		const tmpByCode = new Map();
		(Array.isArray(tmpRoles) ? tmpRoles : [])
			.filter((pRole) => pRole && pRole.PermissionLevel && (pRole.Type !== 'PermissionGroup') && !TIER_LEVELS.includes(pRole.PermissionLevel))
			.forEach((pRole) =>
			{
				if (!tmpByCode.has(pRole.PermissionLevel))
				{
					tmpByCode.set(pRole.PermissionLevel, { Code: pRole.PermissionLevel, Names: [] });
				}
				tmpByCode.get(pRole.PermissionLevel).Names.push(pRole.Name || pRole.PermissionLevel);
			});
		tmpState.Roles = Array.from(tmpByCode.values()).sort((pA, pB) => pA.Names[0].localeCompare(pB.Names[0]));

		const tmpTypes = await this._getJSON(`ReportNamedInstances/LiteExtended/Hash,Name,IDCustomer/FilteredTo/FBL~IDCustomer~INN~0,${Number(pIDCustomer)}~FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/5000`);
		const tmpByHash = new Map();
		(Array.isArray(tmpTypes) ? tmpTypes : []).filter((pType) => pType && pType.Hash).forEach((pType) =>
		{
			const tmpStandard = !(Number(pType.IDCustomer) > 0);
			if (!tmpByHash.has(pType.Hash) || !tmpStandard)
			{
				tmpByHash.set(pType.Hash, { Hash: pType.Hash, Name: pType.Name || pType.Hash, Standard: tmpStandard });
			}
		});
		tmpState.FormTypes = Array.from(tmpByHash.values());
	}

	/** @return {Promise<void>} */
	async saveAsync()
	{
		const tmpState = this.state;
		const tmpConfig = buildAssignmentConfig(tmpState.Loaded_Config, tmpState.Enabled, tmpState.RolesByType);
		await this.writeCustomerConfigAsync(NAMESPACE, NAMESPACE_VALUE, tmpConfig);
		tmpState.Loaded_Config = tmpConfig;
		tmpState.RolesByType = rolesByType(tmpConfig);
		Object.keys(tmpState.Selected).forEach((pHash) =>
		{
			if (!tmpState.RolesByType[pHash])
			{
				delete tmpState.Selected[pHash];
			}
		});
	}

	/** @return {string} */
	savedMessage()
	{
		return 'Saved. Forms created from about 15 minutes from now follow these settings. Forms that already exist keep the access they have.';
	}

	// --- Edits ----------------------------------------------------------------------------------------------------

	/** @param {boolean} pOn */
	setEnabled(pOn)
	{
		this.state.Enabled = !!pOn;
		this.markDirty();
		this.render();
	}

	/**
	 * @param {string} pHash
	 * @param {boolean} pOn
	 */
	selectType(pHash, pOn)
	{
		if (pOn)
		{
			this.state.Selected[pHash] = true;
		}
		else
		{
			delete this.state.Selected[pHash];
		}
		this.render();
	}

	/** @param {boolean} pOn */
	selectAll(pOn)
	{
		this.state.Selected = {};
		if (pOn)
		{
			Object.keys(this.state.RolesByType).forEach((pHash) =>
			{
				this.state.Selected[pHash] = true;
			});
		}
		this.render();
	}

	/** @return {Array<string>} the form types the toolbar acts on */
	selectedTypes()
	{
		return Object.keys(this.state.Selected).filter((pHash) => this.state.RolesByType[pHash]);
	}

	/**
	 * @param {string} pHash
	 * @param {string} pCode
	 */
	removeRole(pHash, pCode)
	{
		const tmpRoles = this.state.RolesByType[pHash] || [];
		this.state.RolesByType[pHash] = tmpRoles.filter((pRole) => pRole !== pCode);
		this.markDirty();
		this.render();
	}

	/** @param {string} pCode - take this role off every selected form type */
	removeRoleFromSelected(pCode)
	{
		if (!pCode)
		{
			return;
		}
		this.selectedTypes().forEach((pHash) =>
		{
			this.state.RolesByType[pHash] = (this.state.RolesByType[pHash] || []).filter((pRole) => pRole !== pCode);
		});
		this.markDirty();
		this.render();
	}

	/** @param {string} pHash - stop granting this form type on create */
	removeType(pHash)
	{
		delete this.state.RolesByType[pHash];
		delete this.state.Selected[pHash];
		this.markDirty();
		this.render();
	}

	/**
	 * @param {string} pPicker - 'FormType' or 'Role'
	 * @param {string} pText
	 * @return {Array<{ Value: string, Label: string, Sub?: string }>} form types not yet listed, or roles some selected type lacks
	 */
	pickerChoices(pPicker, pText)
	{
		const tmpState = this.state;
		if (pPicker === 'FormType')
		{
			const tmpChoices = tmpState.FormTypes
				.filter((pType) => !tmpState.RolesByType[pType.Hash])
				.map((pType) => ({ Value: pType.Hash, Label: pType.Name, Sub: pType.Standard ? 'standard form' : '' }));
			return libCustomerConfigScreen.filterChoices(tmpChoices, pText);
		}
		const tmpSelected = this.selectedTypes();
		const tmpChoices = tmpState.Roles
			.filter((pRole) => tmpSelected.some((pHash) => !(tmpState.RolesByType[pHash] || []).includes(pRole.Code)))
			.map((pRole) => ({ Value: pRole.Code, Label: pRole.Names.join(', '), Sub: pRole.Code }));
		return libCustomerConfigScreen.filterChoices(tmpChoices, pText);
	}

	/**
	 * List a form type (selected, so the next role added reaches it), or add a role to every selected form type.
	 *
	 * @param {string} pPicker - 'FormType' or 'Role'
	 * @param {string} pValue
	 */
	pickerChose(pPicker, pValue)
	{
		const tmpState = this.state;
		if (pPicker === 'FormType')
		{
			if (!tmpState.RolesByType[pValue])
			{
				tmpState.RolesByType[pValue] = [];
			}
			tmpState.Selected[pValue] = true;
			this.markDirty();
			return;
		}
		this.selectedTypes().forEach((pHash) =>
		{
			const tmpRoles = tmpState.RolesByType[pHash] || [];
			if (!tmpRoles.includes(pValue))
			{
				tmpRoles.push(pValue);
			}
			tmpState.RolesByType[pHash] = tmpRoles;
		});
		this.markDirty();
	}

	// --- Rendering ------------------------------------------------------------------------------------------------

	/**
	 * @param {string} pCode
	 * @return {FormAccessRole|null}
	 */
	_role(pCode)
	{
		return this.state.Roles.find((pRole) => pRole.Code === pCode) || null;
	}

	/**
	 * @param {string} pHash
	 * @param {string} pCode
	 * @return {string}
	 */
	_chipHTML(pHash, pCode)
	{
		const tmpRole = this._role(pCode);
		const tmpLabel = tmpRole ? tmpRole.Names.join(', ') : pCode;
		const tmpTitle = tmpRole ? pCode : 'No role has this permission level, so it gives nobody access';
		return `<span class="ccs-chip ${tmpRole ? '' : 'is-unknown'}" title="${this._esc(tmpTitle)}">${this._esc(tmpLabel)}<button class="ccs-chip-x" title="Remove" onclick="_Pict.providers.${this.providerHash}.removeRole(${this._arg(pHash)}, ${this._arg(pCode)})">×</button></span>`;
	}

	/** @return {string} one row per listed form type */
	_tableHTML()
	{
		const tmpState = this.state;
		const tmpTypes = new Map(tmpState.FormTypes.map((pType) => [ pType.Hash, pType ]));
		const tmpHashes = Object.keys(tmpState.RolesByType).sort((pA, pB) => ((tmpTypes.get(pA) || { Name: pA }).Name).localeCompare((tmpTypes.get(pB) || { Name: pB }).Name));
		if (tmpHashes.length < 1)
		{
			return '<div class="ccs-note">No form types are listed, so a new form is open only to its project’s members and the people its approval steps name.</div>';
		}
		const tmpAllSelected = tmpHashes.every((pHash) => tmpState.Selected[pHash]);
		const tmpRows = tmpHashes.map((pHash) =>
		{
			const tmpType = tmpTypes.get(pHash);
			const tmpName = tmpType ?
				`${this._esc(tmpType.Name)}<span class="ccs-sub">${this._esc(pHash)}</span>` :
				`${this._esc(pHash)}<span class="ccs-sub ccs-warn">No form type has this code</span>`;
			const tmpChips = (tmpState.RolesByType[pHash] || []).map((pCode) => this._chipHTML(pHash, pCode)).join('');
			return `<tr class="${tmpState.Selected[pHash] ? 'is-selected' : ''}">
				<td><input type="checkbox" aria-label="Select" ${tmpState.Selected[pHash] ? 'checked' : ''} onchange="_Pict.providers.${this.providerHash}.selectType(${this._arg(pHash)}, this.checked)"></td>
				<td>${tmpName}</td>
				<td><div class="ccs-chips">${tmpChips || '<span class="ccs-sub ccs-warn">No roles yet. Select it and add one, or it is dropped when you save.</span>'}</div></td>
				<td><button class="ccs-btn is-danger" onclick="_Pict.providers.${this.providerHash}.removeType(${this._arg(pHash)})">Remove</button></td>
			</tr>`;
		}).join('');
		return `<table class="ccs-table">
			<thead><tr>
				<th><input type="checkbox" aria-label="Select all" ${tmpAllSelected ? 'checked' : ''} onchange="_Pict.providers.${this.providerHash}.selectAll(this.checked)"></th>
				<th>Form type</th><th>Roles that can open a new one</th><th></th>
			</tr></thead>
			<tbody>${tmpRows}</tbody>
		</table>`;
	}

	/** @return {string} the actions on the selected form types */
	_toolbarHTML()
	{
		const tmpState = this.state;
		const tmpSelected = this.selectedTypes();
		if (tmpSelected.length < 1)
		{
			return '<p class="ccs-help">Select form types to add a role to all of them at once, or to take one off.</p>';
		}
		const tmpPresent = Array.from(new Set([].concat(...tmpSelected.map((pHash) => tmpState.RolesByType[pHash] || []))));
		const tmpRemoveOptions = tmpPresent.map((pCode) =>
		{
			const tmpRole = this._role(pCode);
			return `<option value="${this._esc(pCode)}">${this._esc(tmpRole ? tmpRole.Names.join(', ') : pCode)}</option>`;
		}).join('');
		return `<div class="ccs-row">
			<span><b>${tmpSelected.length}</b> selected. Add a role:</span>
			${this.pickerHTML('Role', 'Search roles')}
		</div>
		${tmpPresent.length ? `<div class="ccs-row"><span>Take a role off the selected forms:</span>
			<select class="ccs-select" onchange="_Pict.providers.${this.providerHash}.removeRoleFromSelected(this.value)"><option value="">Choose a role…</option>${tmpRemoveOptions}</select></div>` : ''}`;
	}

	/** @return {string} */
	bodyHTML()
	{
		const tmpState = this.state;
		const tmpOff = tmpState.Enabled ? '' : '<div class="ccs-note ccs-warn">Turned off: new forms get none of the access below until this is turned back on.</div>';
		const tmpNoRoles = tmpState.Roles.length ? '' : '<div class="ccs-note ccs-warn">None of this customer’s roles has a permission level, so there is nothing to choose yet. Give a role one in Access &amp; Permissions.</div>';
		return `<div class="ccs-section">
			<label class="ccs-check"><input type="checkbox" ${tmpState.Enabled ? 'checked' : ''} onchange="_Pict.providers.${this.providerHash}.setEnabled(this.checked)"> Give new forms to the roles listed below</label>
			${tmpOff}
			<div class="ccs-note">
				Everyone holding a listed role can open and edit a new form of that type, on any project, without being a project member.
				Changes reach forms created from now on. Forms that already exist keep the access they have.
				Roles are matched by their permission level, set on the role in Access &amp; Permissions.
				Lab test forms are also given to the teams that staff the test’s lab (Teams), whatever is listed here.
			</div>
			${tmpNoRoles}
			${this._toolbarHTML()}
			${this._tableHTML()}
			<div class="ccs-row"><span>Add a form type:</span>${this.pickerHTML('FormType', 'Search form types')}</div>
		</div>`;
	}
}

FormAccessConfigProvider.SCREEN =
{
	Hash: 'FormAccessConfig',
	StateKey: 'FormAccessConfig',
	Mount: '#FAC-Detail',
	Entitlement: { Entity: 'FormAccess', Action: 'Configure', Label: 'Configure who can open new forms' },
};

module.exports = FormAccessConfigProvider;
module.exports.default_configuration = { ProviderIdentifier: 'FormAccessConfig', AutoInitialize: true, AutoInitializeOrdinal: 0 };
module.exports.rolesByType = rolesByType;
module.exports.buildAssignmentConfig = buildAssignmentConfig;
